/**
 * Per-request extras for the agent server routes, kept pure for unit tests:
 *  - the signed-in user's access token (`Authorization: Bearer`), which the
 *    server checks when its request authentication is on; absent when there
 *    is no token (MSAL sign-in off),
 *  - the test-user header for agent access control (test-file mode only;
 *    set by app-config.json "agentTestUser", absent otherwise),
 *  - a clinician-readable line for a request the agent server refused
 *    (not signed in, not identified, or a tool the role does not allow).
 */
import { AGENT_TEST_USER_HEADER } from '@agent/tool-names';

/** Headers to send with every agent server request. */
export function agentRequestHeaders(
    testUser: string,
    accessToken = ''
): Record<string, string> {
    const name = testUser.trim();
    return {
        ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
        ...(name ? { [AGENT_TEST_USER_HEADER]: name } : {}),
    };
}

/**
 * The chat transport surfaces a refused request as an Error whose message
 * is the server's JSON body (`{ error: { code, message } }`). Turn it into
 * one readable line; undefined when there is no error.
 */
export function describeAgentError(
    error: Error | undefined
): string | undefined {
    if (!error) {
        return undefined;
    }
    try {
        const body = JSON.parse(error.message) as {
            error?: { code?: string; message?: string };
        };
        const code = body.error?.code;
        const message = body.error?.message;
        if (code === 'unauthenticated') {
            return `The agent did not accept your sign-in: ${message ?? 'not signed in'}.`;
        }
        if (code === 'unauthorized') {
            return `The agent did not recognise you: ${message ?? 'not identified'}.`;
        }
        if (code === 'forbidden_tool') {
            return message ?? 'Your role does not allow this.';
        }
        if (message) {
            return `The agent could not answer: ${message}`;
        }
    } catch {
        // Not a JSON error body (network failure, stream error): fall through.
    }
    return 'The agent could not answer. Try again, or start a new chat.';
}
